<template>
  <li class="explorer-node">
    <a :class="{selected: selected, open: open}"
       @click="click"
       @dblclick="dblclick"
       @contextmenu.stop.prevent="rightClick"
    >
      <i :class="item.directory ? 'folder' : 'doc'" class="icon" />
      <span v-if="!edit" class="node_name">{{ item.name }}</span>

      <input v-if="edit"
             id="name"
             ref="name"
             v-model="editName"
             class="rename"
             autofocus
             @click.prevent
             @keyup.enter="submit"
             @keyup.esc="eventBus.$emit('fileExplorer:navigate')"
      >
      <span v-if="mode==='details'" class="node_right">{{ item.permissions }}</span>
      <span v-if="mode==='details'" class="node_size">{{ item.size | octet }}</span>
      <span v-if="mode==='details'" class="node_date">{{ item.date | date }}</span>
    </a>

    <context-menu ref="contextMenu">
      <li v-if="item.directory" @click="dblclick">{{ $t('g-fileExplorer:open') | capitalize }}</li>
      <li v-if="$store.getters.bscFeature($store.getters.gateway, 'fileExchange') && !item.directory" @click="eventBus.$emit('fileExplorer:download')">{{ $t('g-fileExplorer:download') | capitalize }}</li>
      <li @click="edit=true">{{ $t('g-fileExplorer:rename') | capitalize }}</li>
      <li @click="eventBus.$emit('fileExplorer:copy')">{{ $t('g-fileExplorer:copy') | capitalize }}</li>
      <li @click="eventBus.$emit('fileExplorer:cut')">{{ $t('g-fileExplorer:cut') | capitalize }}</li>
      <li v-if="item.directory" @click="eventBus.$emit('fileExplorer:paste', {directory: item})">{{ $t('g-fileExplorer:paste') | capitalize }}</li>
      <li @click="eventBus.$emit('fileExplorer:delete')">{{ $t('g-fileExplorer:delete') | capitalize }}</li>
    </context-menu>
  </li>
</template>

<script>
import { isEmpty } from 'lodash-es'
import eventBus from '@/modules/eventBus'

export default {
  name: 'ExplorerNode',

  props: {
    // Tree node
    item: {
      type: Object,
      required: true
    },
    // Display mode
    mode: {
      type: String,
      default: 'icons'
    },
    // Click handler
    clickHandler: {
      type: Function,
      default: () => {}
    },
    // Double click handler
    dblclickHandler: {
      type: Function,
      default: () => {}
    }
  },

  data () {
    return {
      open: false,
      selected: false,
      edit: false,
      editName: ''
    }
  },

  computed: {
    eventBus () { return eventBus }
  },

  watch: {
    async edit (newEditValue) {
      if (newEditValue) {
        // Clear all selected and select new item
        eventBus.$emit('fileExplorer:unselect')
        this.selected = true

        // Focus input and select content, on nextTick to let the input be shown
        await this.$nextTick()
        this.$refs.name.focus()
        this.$refs.name.select()
      }
    }
  },

  created () {
    // Using a empty Vue instance as event bus
    // We cannot use a store/props with a watch, it does not work when using the same key several times
    eventBus.$on('fileExplorer:unselect', this.unselect)
    eventBus.$on('fileExplorer:editSelection', this.activateEdit)
    eventBus.$on('fileExplorer:navigate', this.cancel)

    // Duplicate name to keep original
    this.editName = this.item.name

    // If new item
    if (this.item.isNew) {
      this.edit = true
    }
  },

  destroyed () {
    eventBus.$off('fileExplorer:unselect', this.unselect)
    eventBus.$off('fileExplorer:editSelection', this.activateEdit)
    eventBus.$off('fileExplorer:navigate', this.cancel)
  },

  methods: {
    // Click on an item
    click () {
      // Nothing on new item
      if (this.item.isNew) return

      // If not selected, select it
      if (!this.selected) {
        // Clear all selected
        eventBus.$emit('fileExplorer:unselect')

        // Select clicked item
        this.selected = true
      }

      // Call click handler
      if (typeof this.clickHandler === 'function') this.clickHandler(this.item)
    },

    // Double click on an item, will be triggered after click
    dblclick () {
      // Nothing on new item
      if (this.item.isNew) return

      // If not open, open it
      if (!this.open) {
        // Open clicked item
        this.open = true
      }

      // Call double click handler
      if (typeof this.dblclickHandler === 'function') this.dblclickHandler(this.item)
    },

    // Context menu event on right click
    rightClick (event) {
      // Nothing on new item
      if (this.item.isNew) return

      // Trigger a click to select it
      this.click()

      // Open context menu
      this.$refs.contextMenu.open(event)
    },

    // Unselect element
    unselect () {
      this.selected = false
      this.open = false
    },

    // Activate edit mode if selected element
    activateEdit () {
      const selectedElement = this.$store.getters.selectedElement
      if (!isEmpty(selectedElement) && selectedElement.name === this.item.name) {
        this.edit = true
      }
    },

    // Validate edition/creation
    submit () {
      // new and empty name: restore name and save
      // new and name: save
      // old and empty name: restore
      // old and name: save

      // Just cancel and restore original name
      if (this.item.name === '') {
        this.editName = this.item.name
        this.edit = false
      }

      // Save new name
      if (this.editName !== this.item.name) {
        this.edit = false
        this.$store.dispatch(this.item.isNew ? 'createNewFolder' : 'edit', this.editName)
          .then(() => {
            // Unselect elements and refresh
            eventBus.$emit('fileExplorer:unselect')
            this.$store.commit('setSelectedElement', null)
            eventBus.$emit('fileExplorer:refresh')
          })
      }
    },

    // Cancel edition/creation
    cancel () {
      this.editName = this.item.name
      this.edit = false
    }
  }
}
</script>

<style scoped lang="scss">
  @import "~@/scss/variables";

  li {
    display: inline-block;
    padding: 0;
    margin: 0;
    line-height: 17px;
    text-align: left;
    white-space: nowrap;
    list-style: none;
    user-select: none;
    outline: 0;

    a {
      display: inline-block;
      width: 65px;
      height: auto;
      padding: 5px 0;
      margin: 0 10px 10px 0;
      color: $primary;
      text-align: center;
      vertical-align: top;
      cursor: pointer;
      border: none;
      transition: none;

      i.icon {
        display: inline-block;
        width: 41px;
        height: 41px;
        padding: 0;
        margin: 0;
        line-height: 0;
        vertical-align: top;
        cursor: pointer;
        background-color: transparent;
        background-image: url("~@/img/file-explorer-big.png");
        background-repeat: no-repeat;
        background-attachment: scroll;
        border: 0 none;
        outline: none;

        &.folder {
          background-position: 0 0;
        }

        &.doc {
          background-position: -82px 0;
        }
      }

      &.selected {
        background-color: $gray-300;
        border-radius: 10px;
      }

      &.open {
        i.icon.folder {
          background-position: -41px 0;
        }
      }

      span.node_name {
        display: block;
        margin: 0;
        line-height: 14px;
        word-wrap: break-word;
        white-space: normal;
      }

      input.rename {
        display: block;
        width: 80%;
        height: 14px;
        padding: 0 2px;
        margin: 0 auto;
        font-size: 12px;
        border: 1px $gray-500 solid;
      }
    }

    &.list,
    &.details {
      display: list-item;
      padding: 0;

      a {
        width: auto;
        height: 21px;
        padding: 0 3px 0 0;
        margin: 0;
        text-align: left;

        i.icon {
          width: 21px;
          height: 21px;
          vertical-align: bottom;
          background-image: url("~@/img/file-explorer.png");

          &.folder {
            background-position: -147px 0;
          }

          &.doc {
            background-position: -147px -42px;
          }
        }

        &.selected {
          border-radius: $border-radius;
        }

        span.node_name {
          display: inline;
          white-space: nowrap;
        }
      }
    }

    &.details {
      a {
        width: 100%;

        span.node_name {
          display: inline-block;
          width: 45%;
          overflow-x: hidden;
          text-overflow: ellipsis;
          vertical-align: text-top;
        }

        span.node_right {
          display: inline-block;
          width: 15%;
          white-space: nowrap;
        }

        span.node_size {
          display: inline-block;
          width: 15%;
          white-space: nowrap;
        }

        span.node_date {
          display: inline-block;
          width: auto;
          white-space: nowrap;
        }
      }
    }
  }
</style>
